import { useInfiniteQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { stockApi, type StockMovementFilters } from '../../api/stock-api';
import { movementReasonLabels, type StockMovement } from '../../domain/stock';
import type { StockReferences } from './use-stock-references';

function change(movement: StockMovement, references: StockReferences): string {
  const from = movement.fromLocationId ? references.locationName(movement.fromLocationId) : '';
  const to = movement.toLocationId ? references.locationName(movement.toLocationId) : '';
  if (from && to) return `${from} → ${to}`;
  if (to) return `into ${to}`;
  return `out of ${from}`;
}

/** The immutable ledger, newest first, with "Load more". */
export function MovementList({
  filters,
  references,
  showItem = true,
}: {
  filters?: StockMovementFilters;
  references: StockReferences;
  showItem?: boolean;
}) {
  const movements = useInfiniteQuery({
    queryKey: ['stock', 'movements', filters ?? {}],
    queryFn: ({ pageParam }) => stockApi.listStockMovements(filters, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextPageToken,
  });

  if (movements.isLoading) return <p className="empty-note">Loading movements…</p>;
  if (movements.isError) {
    return (
      <p role="alert" className="form-error">
        Movements could not be loaded.{' '}
        <button type="button" onClick={() => void movements.refetch()}>
          Retry
        </button>
      </p>
    );
  }
  const rows = movements.data?.pages.flatMap((page) => page.movements) ?? [];
  if (rows.length === 0) return <p className="empty-note">No movements yet.</p>;

  return (
    <>
      <ul className="movement-list">
        {rows.map((movement) => {
          const outbound = movement.fromLocationId && !movement.toLocationId;
          return (
            <li key={movement.id} className="movement-entry">
              <div className="movement-main">
                <strong className={outbound ? 'movement-qty movement-out' : 'movement-qty'}>
                  {outbound ? '−' : movement.toLocationId && !movement.fromLocationId ? '+' : ''}
                  {movement.quantity} {references.unit(movement.stockItemId)}
                </strong>
                <span className="state-badge">{movementReasonLabels[movement.reason]}</span>
                {showItem ? (
                  <Link to={`/stock/items/${encodeURIComponent(movement.stockItemId)}`}>
                    {references.itemName(movement.stockItemId)}
                  </Link>
                ) : null}
                <span>{change(movement, references)}</span>
              </div>
              <div className="movement-meta">
                {movement.actor ?? '—'}
                {movement.createTime ? ` · ${new Date(movement.createTime).toLocaleString()}` : ''}
                {movement.referenceId ? ` · Ref ${movement.referenceId}` : ''}
                {movement.note ? ` · ${movement.note}` : ''}
              </div>
            </li>
          );
        })}
      </ul>
      {movements.hasNextPage ? (
        <button
          type="button"
          onClick={() => void movements.fetchNextPage()}
          disabled={movements.isFetchingNextPage}
        >
          {movements.isFetchingNextPage ? 'Loading…' : 'Load more'}
        </button>
      ) : null}
    </>
  );
}
